import { Component, OnInit, OnDestroy } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { Ticket } from '@app/modules/model/ticket';
import { SmartDrawerDevice } from '@app/modules/model/smartdrawer';

import { GenericUtils } from '@app/app.commonutils';

import { languageSupport, languageService } from '@app/modules/common/language';
import { viewService } from '@app/modules/view';
import { deviceService, WSConnection } from '@app/modules/sockets';
import { PlaceService } from '@app/modules/model/svcplace';
import { toastService } from '@app/modules/common/toast';
import { waiterLockService } from '@app/modules/waiter-lock.service';
import { Subject, Subscription } from 'rxjs';

@Component({
    selector: 'ui-modal-cashguard-cashpay',
    templateUrl:'./ui-cashguard-cash.html',
    styleUrls: [ 
        './ui-cashguard-cash.scss'
    ]
})
export class UiCashGuardCashPayModal extends languageSupport implements OnInit, OnDestroy {
    public _tickets: Array<Ticket> = [];
    public _device: SmartDrawerDevice = null;
    public _place: PlaceService = null;
    public _existingConnection: WSConnection = null;  // Connection passed from parent
    public _tolocal: boolean = false;                  // LAN vs WSR (parent lo determina via place.smartdrawerForTicket)
    public _cashless: boolean = false;                 // cobro con tarjeta via datafono WIPAY del Cashlogy (no efectivo)
    public _deviceid: string = null;                   // PROSEGUR: alias MDW del datafono (PaymentTerminal, PaymentTerminal2...)
    public _refund: boolean = false;                   // abono cashless: devolucion referenciada por el datafono WIPAY
    private _refundTransactionNumber: string = null;   // transactionNumber del cobro original (de cashlessInfo) — CASHLOGY
    private _refundOrderId: string = null;             // orderId del cobro original (MDW) — PROSEGUR
    private _refundUuid: string = null;                // _uuid del ticket: PROSEGUR cross-device (el back resuelve el orderId por él)
    private _refundPeripheralId: string = null;        // peripheralId del cobro original (opcional)
    // cashlessInfo devuelto por el CHARGE_CASHLESS (authCode, transactionNumber...);
    // lo propagamos en el dismiss para poder cachearlo y reembolsar despues.
    private _cashlessInfo: any = null;
    // orderId de la venta con tarjeta en PROSEGUR (MDW); se necesita para el
    // CardRefund. Lo propagamos en el dismiss para cachearlo.
    private _orderId: string = null;

    // Target del servicio WS = modelo del cajon (CASHGUARD / CASHLOGY / GLORY).
    // El print_service enruta por TARGET (case-insensitive) al modulo del mismo
    // nombre (cashguard/cashlogy). Fallback a CASHGUARD para cajones antiguos
    // sin modelo guardado.
    private get _target(): string {
        return (this._device && this._device.model) ? this._device.model : 'CASHGUARD';
    }

    // Comando enviado al cajon: REFUND (abono cashless referenciado), o el
    // cobro (CHARGE_CASHLESS via datafono / PROCESS_TRANSACTION en efectivo).
    // El back resuelve el peripheralId del CHARGE solo; el REFUND lo acepta
    // opcional (lo pasamos si lo teniamos guardado del cobro).
    private get _txCommand(): string {
        if (this._refund){
            return 'REFUND';
        }
        // Agnostico: el cobro con tarjeta siempre es CHARGE_CASHLESS. Cada
        // driver del back lo traduce a su datafono (cashlogy.rs por ConnectorPlus;
        // prosegur.rs a un Sale del MDW con deviceId=PaymentTerminal).
        return this._cashless ? 'CHARGE_CASHLESS' : 'PROCESS_TRANSACTION';
    }

    // Payload de la transaccion. En REFUND (abono cashless) añade el
    // transactionNumber del cobro original y, si lo teniamos, el peripheralId.
    private _txPayload(transactionId: any, amountDueCents: number): any {
        let _payload: any = {
            command: this._txCommand,
            transaction_id: transactionId,
            amount_due: amountDueCents
        };
        // COBRO con tarjeta: mandamos los `_uuid` ESTABLES de los tickets del
        // cobro. El backend guarda el orderId de tarjeta por CADA `_uuid` para
        // poder DEVOLVER cross-device (comandero cobra, TPV devuelve). El
        // `transaction_id` usa `objref` (el `objid` en tickets ya creados), que
        // NO casa con el `_uuid` que manda la devolución; por eso se envían los
        // `_uuid` aparte y se indexan todos (guest = varios tickets en un cobro).
        if (!this._refund){
            let _uuids = (this._tickets || [])
                .map((t: any) => t && t._uuid)
                .filter((u: any) => !!u);
            if (_uuids.length){
                _payload.uuids = _uuids;
            }
        }
        // PROSEGUR: datafono concreto al que va el cobro. El MDW nombra al
        // segundo y siguientes con sufijo (PaymentTerminal2, PaymentTerminal3).
        // El back solo pasa lo que recibe; sin esto usa PaymentTerminal.
        if (this._deviceid){
            _payload.deviceId = this._deviceid;
        }
        if (this._refund){
            // CASHLOGY referencia por transactionNumber; PROSEGUR por orderId.
            if (this._refundTransactionNumber){
                _payload.transactionNumber = this._refundTransactionNumber;
            }
            if (this._refundOrderId){
                _payload.orderId = this._refundOrderId;
            }
            // PROSEGUR cross-device: sin orderId local (cobró otro dispositivo),
            // mandamos el _uuid del ticket; el backend (print_service) resuelve el
            // orderId de su map ORDENADO por _uuid. El backend prioriza el orderId
            // si viene; si no, cae al _uuid.
            if (this._refundUuid){
                _payload.uuid = this._refundUuid;
            }
            if (this._refundPeripheralId){
                _payload.peripheralId = this._refundPeripheralId;
            }
        }
        return _payload;
    }

    // Textos del modal segun modo (abono cashless / cobro tarjeta datafono / efectivo).
    get PayTitleKey(): string {
        if (this._refund){
            return '@cashless_refund_title';
        }
        return this._cashless ? '@ticket_paycard_datafono_title' : '@ticket_paycashguard_title';
    }
    get WaitLabelKey(): string {
        if (this._refund){
            return '@cashless_refund_waiting';
        }
        return this._cashless ? '@cashguard_waiting_datafono' : '@cashguard_waiting_drawer';
    }
    // Mensaje de exito: abono cashless (mensaje que pide el cert) vs cobro.
    get SuccessLabelKey(): string {
        return this._refund ? '@cashless_refund_success' : '@cashguard_success';
    }
    get IsRefund(): boolean {
        return this._refund;
    }

    private _connection: WSConnection = null;
    // Conexion HTTP original (la que paso el parent o la que abrimos en
    // _initializeCashGuard). Si conseguimos hacer upgrade a streaming WS,
    // _connection apunta al WS y mantenemos esta para desconectarla al
    // destruir el modal. Si el upgrade falla, ambas referencias apuntan a
    // lo mismo y se desconecta una vez.
    private _baseConnection: WSConnection = null;
    private _streamingConnection: WSConnection = null;
    private _subscriptions: Array<Subscription> = [];
    private _transactionId: string = null;
    private _timeouts: Array<any> = []; // Array to track all timeouts
    private _lastProgressTime: number = null; // Track last progress update time
    private _cancelling: boolean = false;
    private _releasingBlocking: boolean = false; // Flag to prevent processing success messages while releasing blocking transactions // Flag to prevent multiple simultaneous cancellations
    // El ultimo error fue CANCELLED (cancelacion del usuario) — cambia el titulo
    // de "Error en el pago" a "Pago cancelado". Se recalcula en cada frame de error.
    private _errorIsCancel: boolean = false;
    // Instante (ms) hasta el que X/ABORTAR quedan deshabilitados. Se arma al
    // PULSAR PAGAR (apertura del modal) y se re-arma en cada envío del cobro,
    // efectivo y tarjeta. Motivo: el cajón/datáfono tarda ~3 s en activar la
    // venta y un cancel en esa ventana lo acepta el MDW de Prosegur pero el
    // adaptador no lo entrega ("Was not possible to send cancel command"): la
    // venta sigue viva, nunca se libera el device y el MDW acaba en POS_IN_USE
    // (cuelgue). Evidencia 2026-10-06 (tarjeta): aborts a 0,6/1,6/2,2 s ->
    // huérfana o cuelgue; a 2,9/3,5 s -> bien. El Rust (prosegur.rs
    // CARD_CANCEL_ARM_SECS) retiene además el cancel de tarjeta si llega de
    // otro dispositivo.
    private static readonly ABORT_ARM_MS = 5000;
    private _abortArmUntil: number = 0;
    // Timer de repintado de la ventana. Va APARTE de _timeouts: el primer frame
    // de progress los limpia en bloque y, sin más frames hasta el vencimiento,
    // Angular no reevaluaría AbortLocked y los botones seguirían grises (Copilot #1812).
    private _abortArmTimer: any = null;

    // Getter publico para que el template pueda saber si hay una cancelacion
    // en vuelo (oculta el boton "Cancelar" durante el release/repay).
    get IsCancelling(): boolean {
        return this._cancelling;
    }

    public status: 'waiting' | 'processing' | 'success' | 'error' | 'cancelled' = 'waiting';
    public currentAmount: number = 0;
    public amountDue: number = 0;
    public amountRefund: number = 0;
    // Cambio que la maquina NO pudo devolver (falta de moneda). El cobro se
    // registra igual —el dinero esta dentro—; hay que entregar este cambio a
    // mano. Llega en el frame de exito del cajon como `changeShortage` (cents).
    public changeShortage: number = 0;
    public errorMessage: string = null;
    public blockingTransactionIds: Array<string> = [];

    private _clearingQueue: boolean = false;
    private _queueReadyToRetry: boolean = false;

    get IsClearingQueue(): boolean {
        return this._clearingQueue;
    }

    get HasQueueConflict(): boolean {
        return this.blockingTransactionIds.length > 0 ||
            (this.errorMessage != null && this._isNotFirstQueuedError(this.errorMessage));
    }

    get CanRetry(): boolean {
        return this.status == 'error' && !this._clearingQueue && !this._cancelling &&
            (this.HasQueueConflict || this._queueReadyToRetry);
    }

    get ShowClearQueueButton(): boolean {
        return this.status == 'error' && this.HasQueueConflict && !this._queueReadyToRetry;
    }

    get AmountToPay(){
        let _totalprice = 0;
        for(let _ticket of this._tickets){
            _totalprice += _ticket.price;
        }
        _totalprice = Math.round(_totalprice * 100) / 100;
        return _totalprice;
    }

    get CanAbort(){
        return this.status == 'waiting' || this.status == 'processing';
    }

    get StatusText(){
        if (this._cancelling){
            return this.tr('@cashguard_aborting');
        }
        switch(this.status){
            case 'waiting':
                return this.tr('@cashguard_waiting');
            case 'processing':
                return this.tr('@cashguard_processing');
            case 'success':
                return this.tr(this.SuccessLabelKey);
            case 'error':
                // CANCELLED de la MDW no es un fallo: es cancelacion del usuario.
                // Mostramos titulo de cancelacion en vez de "Error en el pago"
                // (el subtitulo lleva el mensaje amigable).
                return this._errorIsCancel ? this.tr('@cashguard_cancelled') : this.tr('@cashguard_error');
            case 'cancelled':
                return this.tr('@cashguard_cancelled');
            default:
                return '';
        }
    }
    	
    /************************/
    /* STARTS HERE          */
    /************************/

    constructor(
        private lang: languageService, 
        private modalCtrl: ModalController, 
        private params: NavParams, 
        public view: viewService,
        private devsvc: deviceService,
        private toast: toastService,
        private waiterLock: waiterLockService
    ) {
        super(lang, null);
    }

    async ngOnInit() {
        this._tickets = this.params.get('tickets');
        this._device = this.params.get('device');
        this._place = this.params.get('place');
        this._existingConnection = this.params.get('connection');  // Get existing connection if provided
        this._tolocal = this.params.get('tolocal') ?? false;        // LAN vs WSR para el reconnect si toca
        this._cashless = this.params.get('cashless') ?? false;      // cobro tarjeta via datafono WIPAY
        this._deviceid = this.params.get('deviceId') || null;       // PROSEGUR: datafono destino del cobro
        this._refund = this.params.get('refund') ?? false;          // abono cashless (devolucion referenciada)
        this._refundTransactionNumber = this.params.get('refundTransactionNumber') ?? null;
        this._refundOrderId = this.params.get('refundOrderId') ?? null;
        this._refundPeripheralId = this.params.get('refundPeripheralId') ?? null;
        this._refundUuid = this.params.get('refundUuid') ?? null;       // PROSEGUR cross-device: el back resuelve el orderId por el _uuid del ticket

        // Bloquear X/ABORTAR desde que se pulsa PAGAR (ver _abortArmUntil).
        this._armAbortWindow();

        // Use provided amountDue if available (for mixed payments), otherwise calculate from tickets
        let _providedAmount = this.params.get('amountDue');
        this.amountDue = _providedAmount != null ? _providedAmount : this.AmountToPay;
        this._transactionId = this._generateTransactionId();

        // Cobro con cajón/datáfono en curso: suspende el bloqueo de UI por inactividad
        // mientras dura el pago (el operario no hace clicks porque la máquina trabaja).
        // Se limpia SIEMPRE en ngOnDestroy, así que no deja la sesión abierta de más.
        this.waiterLock.SetPaymentActive(true);

        // Capturar NUESTRO propio modal (somos el top al arrancar) para cerrar SIEMPRE
        // este y no el TOP actual: `modalCtrl.dismiss()` cierra el modal superior, y
        // si tras cancelar A el usuario abre B, el dismiss diferido de A (setTimeout)
        // cerraba B (el nuevo top) -> se cerraba el cobro recien abierto. Cerrando el
        // modal propio se elimina ese race.
        this.modalCtrl.getTop().then(m => { this._ownModal = m || null; }).catch(() => {});

        // No await: show the modal immediately; connection/init runs in background.
        // .catch para no dejar una unhandled rejection si un await interno
        // (ej. upgrade WSR) rechaza — el propio _initializeCashGuard ya pinta
        // el estado de error, aqui solo evitamos la rejection sin capturar.
        this._initializeCashGuard().catch(e => {
            console.error('[SMARTDRAWER] init error', e);
        });
    }

    // Referencia a NUESTRO propio elemento de modal (capturada en ngOnInit).
    private _ownModal: any = null;

    // Cierra ESTE modal (no el top actual, que puede ser otro cobro reabierto).
    private _dismissSelf(data?: any){
        if (this._ownModal && typeof this._ownModal.dismiss === 'function'){
            return this._ownModal.dismiss(data);
        }
        return this.modalCtrl.dismiss(data);
    }
    
    ngOnDestroy(){
        super.OnDestroy();
        if (this._abortArmTimer){
            clearTimeout(this._abortArmTimer);
            this._abortArmTimer = null;
        }

        // Reanudar el bloqueo de UI: el cobro terminó (éxito/cancel/error/cierre). Se
        // limpia SIEMPRE aquí para no dejar la sesión sin bloqueo tras el pago.
        this.waiterLock.SetPaymentActive(false);

        // Clear all timeouts
        this._timeouts.forEach(timeout => {
            if (timeout){
                clearTimeout(timeout);
            }
        });
        this._timeouts = [];

        // Unsubscribe from all subscriptions
        this._subscriptions.forEach(sub => {
            if (sub && sub.unsubscribe){
                sub.unsubscribe();
            }
        });
        this._subscriptions = [];

        // Cancel transaction if still active when component is destroyed
        if (this._transactionId && (this.status == 'waiting' || this.status == 'processing') && !this._cancelling){
            this._cancelTransaction().catch(e => {
                console.error('[SMARTDRAWER] Error cancelling transaction on destroy:', e);
            });
        }

        // Resume keepalive before disconnecting
        if (this._connection && typeof this._connection.ResumeKeepAlive === 'function'){
            this._connection.ResumeKeepAlive();
            console.log('[SMARTDRAWER] Keepalive resumed on destroy');
        }

        // Siempre desconectamos al destruir el modal — incluso si la conexion
        // vino del parent. Ninguno de los parents (tab-select, ui-thumb,
        // panel-payment) la reutiliza tras el dismiss, y omitir el Disconnect
        // causaba un WS leak (se acumulaban conexiones abiertas al rasppi
        // del CashGuard cada vez que se abria/cerraba el modal de cobro).
        if (this._streamingConnection){
            this.devsvc.Disconnect(this._streamingConnection);
            this._streamingConnection = null;
        }
        if (this._baseConnection && this._baseConnection !== this._streamingConnection){
            this.devsvc.Disconnect(this._baseConnection);
        }
        this._baseConnection = null;
        this._connection = null;
    }

    private _generateTransactionId(): string {
        // operationId ÚNICO y estable para el MDW. En el COBRO un ticket NUEVO
        // TODAVÍA NO tiene `objid` (se asigna en el commit, en el server, DESPUÉS
        // de pagar; `objid==null` -> _toInsert), ni `invceno` (se asigna al
        // fiscalizar) ni `orderno`. Por eso usamos `objref`, que devuelve el
        // `objid` cuando ya existe (tickets confirmados, p.ej. devoluciones) y el
        // `_uuid` (id de cliente ESTABLE, único y presente desde la creación)
        // cuando no. Antes, sin objid se caía a un aleatorio 0-999 que NO era
        // único y podía hacer que cobros nuevos/concurrentes compartieran
        // transacción en el MDW (rompiendo enrutado/cancelación). El backend le
        // añade `_seq`.
        if (this._tickets && this._tickets.length > 0){
            let ticket = this._tickets[0];
            let _ref = (ticket as any).objref;
            if (_ref != null && _ref.toString().length > 0){
                return _ref.toString();
            }
        }
        // Último recurso, sin ticket (no debería ocurrir): UUID v4 único.
        return GenericUtils.uuidv4();
    }

    // Formatea el operationId para el MDW: número SOLO si la cadena es
    // COMPLETAMENTE decimal (objid), o la cadena tal cual si no (UUID de un
    // ticket nuevo sin objid, o serie+factura tipo "SEP24-4"). `parseInt` a secas
    // trunca un id no totalmente numérico (parseInt('12ab..')=12,
    // parseInt('12345678-..')=12345678), de modo que dos cobros distintos podrían
    // colapsar en el mismo operationId y colisionar/cancelarse cruzado en el MDW.
    private _wireTxId(id: string): string | number {
        return (typeof id === 'string' && /^\d+$/.test(id)) ? parseInt(id, 10) : id;
    }

    private _systemStatusFromResponse(statusResponse: any): string | null {
        if (!statusResponse?.data){
            return null;
        }
        let _parsed = statusResponse.data;
        if (typeof _parsed === 'string'){
            try { _parsed = JSON.parse(_parsed); } catch(_){ return null; }
        }
        if (_parsed && typeof _parsed === 'object' && _parsed.system_status){
            return _parsed.system_status.status || null;
        }
        return null;
    }

    private _needsCashGuardLogin(message: string): boolean {
        if (!message){
            return false;
        }
        return message.includes('Logged Off') ||
            message.includes('Out of Service') ||
            message.toLowerCase().includes('logged off') ||
            message.toLowerCase().includes('out of service');
    }

    private async _attemptCashGuardLogin(): Promise<boolean> {
        try {
            let loginResponse = await Promise.race([
                this._connection.Send(this._target, { command: 'LOGIN' }),
                new Promise<null>((resolve) => setTimeout(() => resolve(null), 10000))
            ]);
            if (!loginResponse || loginResponse.exec != 'SUCCESS'){
                this.status = 'error';
                this.errorMessage = this.tr('@cashguard_login_failed');
                return false;
            }
            return true;
        } catch(e){
            console.error('[SMARTDRAWER] LOGIN failed:', e);
            this.status = 'error';
            this.errorMessage = this.tr('@cashguard_login_failed');
            return false;
        }
    }

    private async _initializeCashGuard(){
        if (!this._device || !this._place){
            this.status = 'error';
            this.errorMessage = this.tr('@cashguard_no_device');
            return;
        }

        // Reuse existing connection if provided, otherwise create a new one.
        // Connect() ya valida BASE/CHECK — no repetimos STATUS aqui.
        if (this._existingConnection){
            console.log('[SMARTDRAWER] Reusing existing WebSocket connection');
            this._connection = this._existingConnection;
            this._baseConnection = this._connection;
        } else {
            let _host = this._place.wshost;
            let _port = this._place.wsport ? parseInt(this._place.wsport) : 5005;
            let _name = this._place.name;

            // Add timeout for connection (10 seconds)
            let connectionTimeout = setTimeout(() => {
                if (this.status == 'waiting' && !this._connection){
                    this.status = 'error';
                    this.errorMessage = this.tr('@cashguard_connection_failed');
                    console.error('[SMARTDRAWER] Connection timeout');
                }
            }, 10000);
            this._timeouts.push(connectionTimeout);

            // Connect to the WebSocket service
            try {
                this._connection = await Promise.race([
                    this.devsvc.Connect(_host, _port, _name, this._tolocal),
                    new Promise<null>((resolve) => {
                        setTimeout(() => resolve(null), 10000); // 10 second timeout
                    })
                ]);
            } catch(e){
                this.status = 'error';
                this.errorMessage = this.tr('@cashguard_connection_failed');
                console.error('[SMARTDRAWER] Connection error:', e);
                return;
            }
            
            if (!this._connection){
                this.status = 'error';
                this.errorMessage = this.tr('@cashguard_connection_failed');
                return;
            }

            // Clear connection timeout since we connected
            clearTimeout(connectionTimeout);
            this._timeouts = this._timeouts.filter(t => t !== connectionTimeout);

            // Registrar la conexion para ngOnDestroy en cuanto conecta.
            this._baseConnection = this._connection;
        }

        // Opt-in WSR streaming (progress en tiempo real). No bloquea si falla.
        // para recibir streaming (progress + terminal en tiempo real). Si el
        // upgrade falla (Apache sin mod_proxy_wstunnel, access.py version
        // antigua, red lenta...), seguimos con la HTTP — el parche del
        // processResponse → _handleMessage hace que igualmente se procese
        // el terminal del cobro, solo perdemos el feedback de progress.
        if (!this._tolocal){
            // ConnectStreaming itself times out at 4s; racing at 1.5s made WSR
            // fall back to HTTP on slow links and silently drop mid-charge
            // progress (only the terminal RESPONSE arrived).
            let _streamConn = await Promise.race([
                this.devsvc.ConnectRemoteStreaming(this._place.name),
                new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000))
            ]);
            if (_streamConn){
                console.log('[SMARTDRAWER] WSR streaming upgrade OK — using WS for transaction');
                this._streamingConnection = _streamConn;
                this._connection = _streamConn;
            }
            else {
                console.log('[SMARTDRAWER] WSR streaming unavailable — using HTTP (no progress feedback)');
            }
        }

        // Subscribe to connection lost (only if we're still processing)
        // Don't show error if transaction is already completed
        // Show error immediately when connection is lost
        const lostSub = this._connection.OnDisconnected.subscribe(async () => {
            // Only show error if we're still waiting or processing
            // If transaction is already successful, ignore disconnection
            if (this.status == 'waiting' || this.status == 'processing'){
                console.log('[SMARTDRAWER] Connection lost detected, showing error immediately');
                this.status = 'error';
                this.errorMessage = this.tr('@cashguard_connection_lost');
                
                // Show error toast outside the modal (in red)
                this.toast.ShowAlert('danger', this.tr('@cashguard_connection_lost'), 3000);
                
                // Release the transaction to return any money that was introduced
                await this._cancelTransaction();
                
                // Close the modal after a short delay
                setTimeout(() => {
                    this._dismissSelf(null);
                }, 2000);
            } else {
                console.log('[SMARTDRAWER] Connection lost but transaction completed, ignoring error');
            }
        });

        // Subscribe to all messages from CASHGUARD service
        // Also handle direct JSON messages (not wrapped in MESSAGE_DATA)
        const messageSub = this._connection.OnMessageRecv.subscribe(data => {
            // Handle messages from the drawer's module (CASHGUARD/CASHLOGY,
            // segun el modelo) o del servicio (keyser)
            if (data['name'] == this._target || data['name'] == 'CASHGUARD' || data['name'] == 'keyser'){
                // Check if it's a FAILURE message
                if (data['exec'] == 'FAILURE' && data['data']){
                    console.log('[SMARTDRAWER] Processing FAILURE message:', data['data']);
                    // Convert to error format for handling
                    this._handleMessage({
                        name: 'CASHGUARD',
                        exec: 'FAILURE',
                        data: {
                            status: 'error',
                            message: typeof data['data'] === 'string' ? data['data'] : JSON.stringify(data['data'])
                        }
                    });
                } else {
                    // Normal message handling
                    this._handleMessage(data);
                }
            }
            // Also handle direct JSON messages that come without SERVICE_NAME
            // These are progress updates sent directly from the Rust service
            else if (data['data'] && typeof data['data'] === 'object' && data['data']['status']){
                // This is a direct JSON message (progress update)
                this._handleMessage(data);
            }
            // Handle messages with exec == 'FAILURE' even if name doesn't match
            else if (data['exec'] == 'FAILURE' && data['data']){
                console.log('[SMARTDRAWER] Processing FAILURE message (no name match):', data['data']);
                this._handleMessage({
                    name: 'CASHGUARD',
                    exec: 'FAILURE',
                    data: {
                        status: 'error',
                        message: typeof data['data'] === 'string' ? data['data'] : JSON.stringify(data['data'])
                    }
                });
            }
            // Frame RAW del RS con el status a NIVEL SUPERIOR (sin envoltorio
            // name/data): por WSR streaming los frames de progreso/terminal
            // llegan tal cual {status:'progress'|'success'|'error', transactionId,
            // currentAmount,...} (en LAN llegan envueltos en {name,data} y los
            // cazan las ramas de arriba). Los normalizamos a {data:<frame>} para
            // _handleMessage; sin esto el progreso no se veia por el comandero.
            else if (data && typeof data === 'object' && typeof data['status'] === 'string'){
                this._handleMessage({ name: this._target, data: data });
            }
            else {
                console.log('[SMARTDRAWER] Unhandled message format:', data);
            }
        });

        this._subscriptions = [lostSub, messageSub];

        // Start the transaction: Use only PROCESS_TRANSACTION
        try {
            this.status = 'waiting';
            this._errorIsCancel = false;   // limpiar flag de un intento anterior

            // Sin conexion establecida (cajon no responde / timeout de conexion):
            // no intentar arrancar la transaccion -> evita el null-deref en
            // _connection.Send ("Cannot read properties of null (reading 'Send')").
            // Mostramos el error (con "Cobrar manual" disponible en ese estado).
            if (!this._connection){
                this.status = 'error';
                this.errorMessage = this.tr('@cashguard_connection_failed');
                console.error('[SMARTDRAWER] No connection when starting transaction');
                return;
            }

            // Convert amount to cents (multiply by 100)
            let amountDueCents = Math.round(this.amountDue * 100);

            // PRE-CHECK de cambio: lo hace el BACKEND (Rust) antes de arrancar la
            // venta, con la CACHÉ del dispensable —sin un CONTENTS en CADA cobro—
            // y devuelve CASH_SHORTAGE si el cajón no puede dar el cambio (mismo
            // aviso @cashguard_cash_shortage vía _friendlyRawError, con COBRAR
            // MANUAL disponible). Antes lo hacía AQUÍ un CONTENTS síncrono que,
            // al esperar el settle de la venta anterior, metía ~3-4s de retardo
            // antes del PROCESS_TRANSACTION. El back es además más completo
            // (comprueba total + céntimos + billetes comunes).

            // Process the transaction (this will trigger progress updates)
            // Note: PROCESS_TRANSACTION sends progress updates directly without UUID
            // The final response with UUID may contain a simple string in MESSAGE_DATA
            this.status = 'processing';

            let { startTimeout, progressTimeout } = this._armTransactionWatchdogs();

            // Número solo si es COMPLETAMENTE decimal (objid); si es UUID o
            // serie+factura se envía la cadena tal cual, sin truncar por parseInt.
            let transactionId = this._wireTxId(this._transactionId);

            // Pause keepalive during transaction to avoid PING interference
            if (this._connection && typeof this._connection.PauseKeepAlive === 'function'){
                this._connection.PauseKeepAlive();
                console.log('[SMARTDRAWER] Keepalive paused for transaction');
            }

            // Limpiar transacciones colgadas de cobros anteriores antes de encolar la nuestra.
            // La API REST exige que solo la primera en cola pueda iniciarse (doc 4.2/8).
            await this._preflightClearStaleTransactions();

            // El arranque es async (el preflight de arriba hace await). Si el
            // modal se DESTRUYE en ese hueco (abort/X, conexión perdida que
            // auto-cierra, cierre rápido), ngOnDestroy pone this._connection a
            // null; sin esta guarda el Send de abajo reventaba con
            // "Cannot read properties of null (reading 'Send')" enmascarado como
            // "Error starting transaction". Si ya no hay conexión, no hay nada
            // que iniciar: salimos en silencio (el modal ya está cerrándose).
            if (this._cancelling || !this._connection){
                console.warn('[SMARTDRAWER] Conexión cerrada o cobro cancelado antes de iniciar la transacción — se aborta el arranque');
                return;
            }

            // En modo WSR (HTTP) el Send no resuelve hasta el mensaje terminal
            // (access.py descarta progress intermedios y solo devuelve
            // success/error/cancelled). El timeout cubre el caso extremo de
            // que la HTTP request quede colgada — debe alinearse con el
            // _TOTAL_TIMEOUT del servidor (15 min) — cliente corta antes
            // a 5 min para no dejar el modal colgado indefinidamente.
            this._armAbortWindow();

            let processResponse = await Promise.race([
                this._connection.Send(this._target, this._txPayload(transactionId, amountDueCents)),
                new Promise<null>((resolve) => {
                    setTimeout(() => resolve(null), 300000); // 5 minutos
                })
            ]);

            // Clear start timeout since we got a response
            clearTimeout(startTimeout);
            this._timeouts = this._timeouts.filter(t => t !== startTimeout);

            // The response from PROCESS_TRANSACTION may be null or contain a simple string
            // This is normal - the actual transaction status comes via progress messages
            if (processResponse){
                console.log('[SMARTDRAWER] Process transaction response:', processResponse);
                // En modo WSR (HTTP), este es el unico canal por el que llega
                // el terminal: alimentamos a _handleMessage para que procese
                // success/error igual que haria via OnMessageRecv en modo LAN.
                // En modo LAN el dedup interno (status === 'success') evita
                // procesar dos veces.
                if (processResponse.exec == 'SUCCESS' && processResponse.data){
                    this._handleMessage(processResponse);
                }
                // If we get a response but haven't received a success message yet,
                // check if it's an error
                if (processResponse.exec != 'SUCCESS' && this.status == 'processing'){
                    let errorMessage = processResponse.data || '';
                    
                    // Check if error is 503 or "Failed to open device" - need to fallback to cash modal
                    if (errorMessage.includes('503') || errorMessage.includes('Service Unavailable') || errorMessage.includes('Failed to open device')){
                        console.log('[SMARTDRAWER] Device error detected in PROCESS_TRANSACTION, closing modal for fallback to cash payment');
                        this.status = 'error';
                        this.errorMessage = errorMessage;
                        // Resume keepalive before closing
                        if (this._connection && typeof this._connection.ResumeKeepAlive === 'function'){
                            this._connection.ResumeKeepAlive();
                        }
                        // Close modal with fallback flag
                        setTimeout(() => {
                            this._dismissSelf({ fallback: true });
                        }, 1000);
                        return;
                    }

                    if (this._needsCashGuardLogin(errorMessage)){
                        console.log('[SMARTDRAWER] LOGIN required — retrying PROCESS_TRANSACTION after session open');
                        if (await this._attemptCashGuardLogin()){
                            // Mismo riesgo de carrera: _attemptCashGuardLogin hace
                            // await; si el modal se destruyó, no reintentar el Send.
                            // _cancelling también: _cancelTransaction lo pone a true
                            // mientras el modal se cierra pero la conexión sigue viva
                            // unos ms; sin esto, un error de LOGIN tardío podía volver
                            // a 'processing' y mandar un 2º PROCESS_TRANSACTION tras el
                            // abort del cajero. La cancelación gana la carrera.
                            if (this._cancelling || !this._connection){
                                console.warn('[SMARTDRAWER] Conexión cerrada o cobro cancelado antes del reintento tras login — se aborta');
                                return;
                            }
                            this.status = 'processing';
                            this.errorMessage = null;
                            this._armAbortWindow();
                            let _loginRetry = await Promise.race([
                                this._connection.Send(this._target, this._txPayload(transactionId, amountDueCents)),
                                new Promise<null>((resolve) => setTimeout(() => resolve(null), 300000))
                            ]);
                            if (_loginRetry?.exec == 'SUCCESS' && _loginRetry.data){
                                this._handleMessage(_loginRetry);
                            } else if (_loginRetry && _loginRetry.exec != 'SUCCESS'){
                                let _retryErr = _loginRetry.data || '';
                                if (!_retryErr.includes('already being processed') && !_retryErr.includes('already processed')){
                                    clearTimeout(progressTimeout);
                                    this._timeouts = this._timeouts.filter(t => t !== progressTimeout);
                                    this.status = 'error';
                                    this.errorMessage = _retryErr ? this._friendlyRawError(_retryErr) : this.tr('@cashguard_start_failed');
                                }
                            }
                        }
                        return;
                    }
                    
                    // Check if the error is "not first queued" - need to release blocking transaction
                    if (errorMessage.includes('not first queued') || errorMessage.includes('TransactionId') && errorMessage.includes('not first')){
                        console.log('[SMARTDRAWER] Transaction not first in queue, releasing blocking transaction...');
                        
                        // Set flag to ignore success messages while releasing blocking transactions
                        this._releasingBlocking = true;
                        
                        // Release the blocking transaction and retry
                        let released = await this._releaseBlockingTransaction(transactionId);
                        
                        if (released){
                            console.log('[SMARTDRAWER] Blocking transaction released, retrying PROCESS_TRANSACTION...');

                            // _releaseBlockingTransaction hizo await: si el modal
                            // se destruyó, no reintentar (evita el null.Send).
                            if (this._cancelling || !this._connection){
                                console.warn('[SMARTDRAWER] Conexión cerrada o cobro cancelado antes del reintento por cola — se aborta');
                                this._releasingBlocking = false;
                                return;
                            }

                            // Clear the error state and reset to processing before retry
                            this.status = 'processing';
                            this.errorMessage = null;

                            // Retry PROCESS_TRANSACTION after releasing the blocking transaction
                            // Note: PROCESS_TRANSACTION sends progress updates directly, so a null response
                            // or timeout doesn't necessarily mean failure - the transaction may be processing
                            this._armAbortWindow();
                            let retryResponse = await Promise.race([
                                this._connection.Send(this._target, this._txPayload(transactionId, amountDueCents)),
                                new Promise<null>((resolve) => {
                                    setTimeout(() => resolve(null), 10000); // 10 second timeout
                                })
                            ]);
                            
                            // Clear flag after retry is sent (success messages can now be processed)
                            this._releasingBlocking = false;
                            
                            if (retryResponse && retryResponse.exec != 'SUCCESS'){
                                let errorMsg = retryResponse.data || '';
                                
                                // "already being processed" means the transaction is already in progress
                                // This is actually a success case - we should continue waiting for progress
                                if (errorMsg.includes('already being processed') || errorMsg.includes('already processed')){
                                    console.log('[SMARTDRAWER] Transaction already being processed, continuing to wait for progress updates...');
                                    // Don't clear progressTimeout - let it continue monitoring
                                    // Status is already 'processing', so we just wait for progress messages
                                    // This is fine - the transaction is already running
                                    // DO NOT set status to 'error' - keep it as 'processing' to continue waiting
                                } else {
                                    // Real error - stop and show error, but DON'T close modal automatically
                                    // User should see the error and decide what to do
                                    console.error('[SMARTDRAWER] Retry failed with error:', errorMsg);
                                    clearTimeout(progressTimeout);
                                    this._timeouts = this._timeouts.filter(t => t !== progressTimeout);
                                    this.status = 'error';
                                    if (this._isNotFirstQueuedError(errorMsg)){
                                        await this._setQueueConflictError(errorMsg);
                                    } else {
                                        this.errorMessage = errorMsg ? this._friendlyRawError(errorMsg) : this.tr('@cashguard_start_failed');
                                    }
                                    // DO NOT close modal - let user see the error and decide
                                }
                            } else {
                                // Retry succeeded or no explicit error (transaction may be processing)
                                // Continue with normal flow - progress messages will come via _handleMessage
                                console.log('[SMARTDRAWER] Retry successful or processing started, waiting for progress updates...');
                                // Don't clear progressTimeout - let it continue monitoring
                                // Status is already 'processing', so we just wait for progress messages
                            }
                        } else {
                            // Failed to release blocking transaction
                            this._releasingBlocking = false; // Clear flag even on failure
                            clearTimeout(progressTimeout);
                            this._timeouts = this._timeouts.filter(t => t !== progressTimeout);
                            this.status = 'error';
                            await this._setQueueConflictError(errorMessage);
                        }
                    } else {
                        // Other error, don't retry. Traducir el resultado MDW si el
                        // `data` crudo lo trae (CANCELLED/CASH_SHORTAGE/...): este
                        // camino (exec!=SUCCESS) no pasa por el frame status:'error'.
                        clearTimeout(progressTimeout);
                        this._timeouts = this._timeouts.filter(t => t !== progressTimeout);
                        this.status = 'error';
                        this.errorMessage = errorMessage ? this._friendlyRawError(errorMessage) : this.tr('@cashguard_start_failed');
                    }
                }
            }

        } catch(e){
            this.status = 'error';
            this.errorMessage = this.tr('@cashguard_start_failed');
            console.error('[SMARTDRAWER] Error starting transaction:', e);
        }
    }

    private _handleMessage(data: any){
        try {
            // Handle null or undefined data
            if (!data){
                console.warn("[SMARTDRAWER] Received null or undefined message data");
                return;
            }

            let messageData = data['data'];
            
            // Handle null or undefined messageData
            if (messageData == null || messageData === undefined){
                console.log("[SMARTDRAWER] Received message with null/undefined data (ignoring)");
                return;
            }
            
            // Messages can come in different formats:
            // 1. Direct JSON object: {"status": "progress", "currentAmount": 50, ...} - from Rust service
            // 2. String that needs parsing: "{\"status\": \"progress\", ...}"
            // 3. Inside MESSAGE_DATA as string: MESSAGE_DATA contains a simple string (not JSON) - ignore this
            
            // If MESSAGE_DATA is a simple string (not JSON), ignore it
            if (typeof messageData === 'string'){
                // Try to parse as JSON first
                try {
                    messageData = JSON.parse(messageData);
                } catch(e){
                    // If it's not valid JSON, it's probably a simple string like "Transaction processed successfully"
                    // This is just a confirmation message, we can ignore it since we already got the success message
                    console.log("[SMARTDRAWER] Received non-JSON message (ignoring):", messageData);
                    return;
                }
            }

            // Handle direct JSON messages (from process_transaction progress updates)
            if (messageData && typeof messageData === 'object'){
                // Ignore empty objects {} - these are just confirmation messages
                // The actual transaction status was already sent directly
                if (Object.keys(messageData).length === 0){
                    console.log("[SMARTDRAWER] Received empty confirmation message (ignoring)");
                    return;
                }
                
                if (messageData.status){
                    // ACK de alcance del cajón (check de conexión del WSR): confirma
                    // que el print_service recibió el comando. No es progreso ni
                    // resultado -> se ignora (en local llega crudo por este camino).
                    if (messageData.status === 'ack'){
                        return;
                    }
                    switch(messageData.status){
                        case 'progress':
                            // Ignorar progress de OTRA transaccion (el cajon es
                            // compartido: PispasService es singleton de proceso) o
                            // posterior a un terminal ya procesado: sin esto, un
                            // progress tardio tras un success revierte status a
                            // 'processing', rearma el timeout de 2 min y su callback
                            // llama _cancelTransaction -> devuelve un cobro YA completado.
                            let _pgTxId = messageData.transactionId || messageData.transaction_id;
                            if (_pgTxId && this._transactionId && _pgTxId != this._transactionId && _pgTxId != this._transactionId.toString()){
                                return;
                            }
                            if (this.status == 'success' || this.status == 'cancelled' || this.status == 'error'){
                                return;
                            }
                            console.log('[SMARTDRAWER][progress] messageData=', messageData,
                                        'currentAmount param=', messageData.currentAmount);
                            if (messageData.currentAmount !== undefined){
                                // Convert from cents to euros
                                this.currentAmount = messageData.currentAmount / 100;
                                this.status = 'processing';
                                this._lastProgressTime = Date.now(); // Update last progress time
                                
                                // Reset progress timeout (give another 2 minutes from last progress)
                                // Clear existing progress timeout
                                this._timeouts = this._timeouts.filter(t => {
                                    try {
                                        clearTimeout(t);
                                        return false;
                                    } catch(e){
                                        return true;
                                    }
                                });
                                
                                // Add new progress timeout (2 minutes)
                                let progressTimeout = setTimeout(async () => {
                                    if (this.status == 'processing'){
                                        console.error('[SMARTDRAWER] No progress timeout (2 minutes) - releasing transaction and closing modal');
                                        this.status = 'error';
                                        this.errorMessage = this.tr('@cashguard_transaction_error');
                                        
                                        // Show error toast outside the modal (in red)
                                        this.toast.ShowAlert('danger', this.tr('@cashguard_transaction_error'), 3000);
                                        
                                        // Release the transaction to return any money that was introduced
                                        await this._cancelTransaction();
                                        
                                        // Close the modal after a short delay
                                        setTimeout(() => {
                                            this._dismissSelf(null);
                                        }, 1000);
                                    }
                                }, 120000); // 2 minutes = 120000ms
                                this._timeouts.push(progressTimeout);
                            }
                            break;
                        case 'success':
                            // Un 'success' que llega DESPUÉS de que el cajero abortó (X):
                            // _cancelTransaction ya puso _cancelling=true y mandó
                            // RELEASE_TRANSACTION, pero el modal sigue abierto esperando la
                            // confirmación del cancel. Sin este guard, un success tardío del
                            // cajón/datáfono (el cliente metió el último billete justo al
                            // abortar) se aceptaba y AcceptModal registraba el cobro CONTRA la
                            // voluntad del cajero. Lo ignoramos: el RELEASE devuelve el dinero.
                            if (this._cancelling){
                                console.log("[SMARTDRAWER] Ignorando success tardío: abort en curso (_cancelling)");
                                return;
                            }

                            // Ignore success messages while releasing blocking transactions
                            if (this._releasingBlocking){
                                console.log("[SMARTDRAWER] Ignoring success message - currently releasing blocking transactions");
                                return;
                            }

                            // Only process success if we haven't already processed it
                            // Also verify that we have a valid transaction ID to prevent false positives
                            if (this.status !== 'success' && this._transactionId){
                                // EXIGIR transactionId en el mensaje. Sin el, este 'success'
                                // no viene de un PROCESS_TRANSACTION — es la respuesta raw
                                // de un STATUS/RESET/CONTENTS/GET_QUEUE que el rasppi manda
                                // con status:'success' generico. Si lo procesabamos como
                                // pago completado, el modal cerraba en init antes de cobrar.
                                let successTransactionId = messageData.transactionId || messageData.transaction_id;
                                if (!successTransactionId){
                                    // Generic success (STATUS/RESET/CONTENTS/GET_QUEUE) — ignore
                                    // so it is not treated as a completed payment.
                                    return;
                                }
                                // Verify the transaction ID matches (to prevent processing success from other transactions)
                                if (successTransactionId != this._transactionId && successTransactionId != this._transactionId.toString()){
                                    console.warn('[SMARTDRAWER] Received success message for different transaction:', successTransactionId, 'expected:', this._transactionId);
                                    return; // Ignore success message for different transaction
                                }
                                
                                // Also verify that we're not in an error state from "not first queued"
                                // If we just failed with "not first queued", we should wait for the retry
                                if (this.status == 'error' && this.errorMessage && this.errorMessage.includes('not first queued')){
                                    console.log("[SMARTDRAWER] Ignoring success message - transaction failed with 'not first queued', waiting for retry");
                                    return;
                                }
                                
                                // Clear all timeouts since transaction completed
                                this._timeouts.forEach(timeout => {
                                    if (timeout){
                                        clearTimeout(timeout);
                                    }
                                });
                                this._timeouts = [];

                                this.status = 'success';
                                // Update currentAmount to the final amount received.
                                // Aceptamos varios nombres porque la respuesta varia segun
                                // version del servicio (Rust antiguo no traia amountDue en el
                                // terminal, nuevo lo trae camelCase, Python lo manda snake_case).
                                // Si no llega ninguno (ej. WSR HTTP fallback sin progress
                                // streaming + rasppi antigua) caemos al amountDue de la modal
                                // que es lo que pedimos al iniciar — al menos eso se pago.
                                let _finalAmount = messageData.currentAmount
                                    ?? messageData.amountDue
                                    ?? messageData.amount_due
                                    ?? messageData.current_amount;
                                console.log('[SMARTDRAWER][success] messageData=', messageData,
                                            'finalAmount=', _finalAmount,
                                            'pre currentAmount=', this.currentAmount,
                                            'pre amountRefund=', this.amountRefund);
                                if (_finalAmount !== undefined && _finalAmount !== null){
                                    this.currentAmount = _finalAmount / 100;
                                }
                                else if (!this.currentAmount){
                                    // Fallback final: la modal ya sabe el importe pedido.
                                    this.currentAmount = this.amountDue;
                                }
                                // Update refund amount if present (acepta camelCase y snake_case).
                                let _refund = messageData.amountRefund ?? messageData.amount_refund;
                                if (_refund !== undefined && _refund !== null && _refund > 0){
                                    this.amountRefund = _refund / 100;
                                }
                                // Cambio que la maquina no pudo devolver (CASH_SHORTAGE):
                                // el cobro se registra igual, pero hay que dar este cambio
                                // a mano. Se avisa en la modal (ver plantilla).
                                let _shortage = messageData.changeShortage ?? messageData.change_shortage;
                                if (_shortage !== undefined && _shortage !== null && _shortage > 0){
                                    this.changeShortage = _shortage / 100;
                                }
                                // Guardar cashlessInfo (transactionNumber/authCode) del cobro
                                // cashless para poder reembolsarlo despues (comando REFUND). Lo
                                // propaga AcceptModal en el dismiss para que el parent lo cachee.
                                if (messageData.cashlessInfo){
                                    this._cashlessInfo = messageData.cashlessInfo;
                                }
                                // PROSEGUR: el cobro con tarjeta devuelve `orderId`
                                // (MDW), necesario para el CardRefund posterior.
                                if (messageData.orderId){
                                    this._orderId = messageData.orderId;
                                }
                                console.log('[SMARTDRAWER][success] post currentAmount=', this.currentAmount,
                                            'post amountRefund=', this.amountRefund);
                                
                                // Resume keepalive now that transaction is complete
                                if (this._connection && typeof this._connection.ResumeKeepAlive === 'function'){
                                    this._connection.ResumeKeepAlive();
                                }
                                
                                // Auto-cerrar a los 2s tanto en pago normal como
                                // en combinado. Antes el combinado exigia pulsar
                                // ACEPTAR a mano — inconsistente con el flujo de
                                // efectivo normal, que se cierra solo. Los 2s dan
                                // tiempo a ver el importe y el cambio a devolver.
                                // EXCEPCION: si la maquina no pudo devolver el cambio
                                // (changeShortage), NO auto-cerramos — el operario debe
                                // ver el aviso y entregar el cambio a mano antes de
                                // cerrar pulsando ACEPTAR.
                                if (this.changeShortage > 0){
                                    console.log("[SMARTDRAWER] Change shortage; requiere ACEPTAR manual para entregar el cambio a mano");
                                } else {
                                    let waitTime = 2000;
                                    let closeTimeout = setTimeout(() => {
                                        console.log("[SMARTDRAWER] Auto-closing modal after successful transaction");
                                        this.AcceptModal();
                                    }, waitTime);
                                    this._timeouts.push(closeTimeout);
                                }
                            } else {
                                if (this.status === 'success'){
                                    console.log("[SMARTDRAWER] Success message already processed, ignoring duplicate");
                                } else if (!this._transactionId){
                                    console.warn("[SMARTDRAWER] Received success message but no transaction ID set");
                                }
                            }
                            break;
                        case 'error':
                            // Ignorar error de OTRA transaccion (cajon compartido).
                            let _erTxId = messageData.transactionId || messageData.transaction_id;
                            if (_erTxId && this._transactionId && _erTxId != this._transactionId && _erTxId != this._transactionId.toString()){
                                return;
                            }
                            // Only process error if we're still processing
                            if (this.status == 'waiting' || this.status == 'processing'){
                                // Clear all timeouts since transaction failed
                                this._timeouts.forEach(timeout => {
                                    if (timeout){
                                        clearTimeout(timeout);
                                    }
                                });
                                this._timeouts = [];

                                this.status = 'error';
                                // Traducir el codigo de resultado de la MDW (Prosegur) a un
                                // mensaje claro. El backend manda el crudo "Sale finished with
                                // result CANCELLED" / "...CASH_SHORTAGE"; sin esto el operario
                                // veia el texto tecnico en ingles. CANCELLED ademas conmuta el
                                // titulo a cancelacion (ver StatusText).
                                // Cajón INACCESIBLE: el WSR responde "unreachable" (TPV
                                // destino apagado / socket ghost, detectado por el check
                                // de alcance de 5s) o "not connected". Mensaje claro en
                                // vez del texto técnico en inglés, para que el operario
                                // sepa que el cajón no está disponible (no un fallo de pago).
                                let _rawErr = (messageData.message || '') + '';
                                if (this._isMdwUnreachableError(_rawErr)){
                                    this._errorIsCancel = false;
                                    this.errorMessage = this.tr('@cashguard_mdw_unreachable');
                                } else if (_rawErr.includes('unreachable') || _rawErr.includes('not connected')){
                                    this._errorIsCancel = false;
                                    this.errorMessage = this.tr('@cashguard_connection_failed');
                                } else {
                                    let _mdwCode = this._mdwResultCode(messageData);
                                    this._errorIsCancel = (_mdwCode === 'CANCELLED');
                                    let _mdwFriendly = this._mdwFriendlyMessage(_mdwCode);
                                    this.errorMessage = _mdwFriendly || messageData.message || this.tr('@cashguard_transaction_error');
                                }

                                // Check if error is 503 or "Failed to open device" - need to fallback to cash modal
                                let errorMsg = this.errorMessage || '';
                                if (errorMsg.includes('503') || errorMsg.includes('Service Unavailable') || errorMsg.includes('Failed to open device')){
                                    console.log('[SMARTDRAWER] Device error detected, closing modal for fallback to cash payment');
                                    // Resume keepalive before closing
                                    if (this._connection && typeof this._connection.ResumeKeepAlive === 'function'){
                                        this._connection.ResumeKeepAlive();
                                    }
                                    // Close modal with fallback flag
                                    setTimeout(() => {
                                        this._dismissSelf({ fallback: true });
                                    }, 1000);
                                    return;
                                }

                                // Resume keepalive on error
                                if (this._connection && typeof this._connection.ResumeKeepAlive === 'function'){
                                    this._connection.ResumeKeepAlive();
                                    console.log('[SMARTDRAWER] Keepalive resumed after transaction error');
                                }
                            }
                            break;
                        case 'cancelled':
                            // Ignorar cancelled de OTRA transaccion (cajon compartido).
                            let _cnTxId = messageData.transactionId || messageData.transaction_id;
                            if (_cnTxId && this._transactionId && _cnTxId != this._transactionId && _cnTxId != this._transactionId.toString()){
                                return;
                            }
                            // Cancelacion limpia: status='cancelled' (no 'error') +
                            // limpiar timeouts pendientes + resume keepalive. El UI
                            // ya tiene case 'cancelled' en statusMessage que muestra
                            // @cashguard_cancelled correctamente.
                            if (this.status == 'waiting' || this.status == 'processing'){
                                this._timeouts.forEach(timeout => {
                                    if (timeout){
                                        clearTimeout(timeout);
                                    }
                                });
                                this._timeouts = [];

                                this.status = 'cancelled';
                            }
                            if (this._connection && typeof this._connection.ResumeKeepAlive === 'function'){
                                this._connection.ResumeKeepAlive();
                                console.log('[SMARTDRAWER] Keepalive resumed after transaction cancellation');
                            }
                            break;
                    }
                }
            }
        } catch(e){
            console.error("[SMARTDRAWER] Error handling message:", e, "Data:", data);
        }
    }

    private _clearAllTimeouts(){
        this._timeouts.forEach(timeout => {
            if (timeout){
                clearTimeout(timeout);
            }
        });
        this._timeouts = [];
    }

    // ------------------------------------------------------------------
    // Resultados de la MDW (Prosegur) — mapeo a mensajes claros
    // ------------------------------------------------------------------

    // Codigo de resultado de la MDW. El backend puede mandarlo en
    // messageData.result / resultCode o embebido en el texto del mensaje
    // ("Sale finished with result CANCELLED").
    private _mdwResultCode(messageData: any): string {
        let _r = (messageData && (messageData.result || messageData.resultCode) || '').toString().toUpperCase();
        if (!_r && messageData && messageData.message){
            let _m = messageData.message.toString().toUpperCase();
            // Mas especifico primero (INCOMPLETE_TRANSACTION antes que cualquier
            // 'TRANSACTION', CASH_SHORTAGE antes que otros).
            const _codes = ['CASH_SHORTAGE', 'INCOMPLETE_TRANSACTION', 'CASH_REGISTER_OPEN_REQUIRED',
                            'DEVICE_NON_INITIALIZED', 'INVALID_AMOUNT', 'TRANSACTION_IN_PROGRESS',
                            // TERMINAL_ABORTED antes que CANCELLED: cancelación NO pedida por el
                            // operario (datáfono apagado/reiniciando o cancelado en el pinpad).
                            'TERMINAL_ABORTED', 'DEVICE_OFFLINE', 'MIDDLEWARE_UNREACHABLE', 'CANCELLED', 'CLAIMED', 'BUSY', 'NACK'];
            for (let _c of _codes){
                if (_m.includes(_c)){ _r = _c; break; }
            }
        }
        return _r;
    }

    // Mensaje amigable por codigo. null => usar el mensaje crudo/fallback.
    private _mdwFriendlyMessage(code: string): string | null {
        switch (code){
            case 'TERMINAL_ABORTED':            return this.tr('@cashguard_terminal_aborted');
            // PROSEGUR: el MDW lo devuelve (~38 s) con el datáfono/cajón apagado o
            // sin conexión. Antes salía el crudo "Sale finished with result DEVICE_OFFLINE".
            // NACK: el datáfono no contesta al MDW (apagado/sin red); mismo mensaje.
            case 'DEVICE_OFFLINE':
            case 'NACK':                        return this.tr('@cashguard_device_offline');
            // Middleware de Prosegur parado/inaccesible (sonda del back antes de la
            // Sale). También por código, no solo por el texto crudo (Copilot #1812).
            case 'MIDDLEWARE_UNREACHABLE':      return this.tr('@cashguard_mdw_unreachable');
            case 'CANCELLED':                   return this.tr('@cashguard_cancelled_user');
            case 'CASH_SHORTAGE':               return this.tr('@cashguard_cash_shortage');
            case 'INCOMPLETE_TRANSACTION':      return this.tr('@cashguard_incomplete');
            case 'CASH_REGISTER_OPEN_REQUIRED': return this.tr('@cashguard_register_open');
            case 'BUSY':                        return this.tr('@cashguard_busy_retry');
            case 'CLAIMED':                     return this.tr('@cashguard_claimed');
            default:                            return null;
        }
    }

    // Aplica el mapeo MDW a un texto CRUDO (el `data` de un PROCESS_TRANSACTION
    // con exec!=SUCCESS, p.ej. "Sale finished with result CANCELLED", que NO pasa
    // por el frame status:'error' de _handleMessage): devuelve el mensaje amigable
    // y marca `_errorIsCancel` si fue cancelacion; si no reconoce ningun codigo
    // devuelve el crudo tal cual.
    // MIDDLEWARE DE PROSEGUR PARADO/INACCESIBLE: el back lo detecta con una sonda
    // antes de la Sale y responde MIDDLEWARE_UNREACHABLE (o, si llega en crudo,
    // el 400 "Failed to log in with Middleware POS… Verify port/service" de la
    // WebAPI). Antes el POST Sale se quedaba colgado y el operario esperaba
    // minutos sin saber que el servicio estaba caído.
    private _isMdwUnreachableError(raw: string): boolean {
        let _r = (raw || '') + '';
        return _r.includes('MIDDLEWARE_UNREACHABLE')
            || _r.includes('Failed to log in with Middleware')
            || _r.includes('Verify port/service');
    }

    private _friendlyRawError(raw: string): string {
        if (this._isMdwUnreachableError(raw)){
            this._errorIsCancel = false;
            return this.tr('@cashguard_mdw_unreachable');
        }
        // Cajón INACCESIBLE por el fallback HTTP: el WSR responde en crudo
        // "Client … unreachable" / "not connected" (TPV apagado/ghost). Mismo
        // mensaje claro que en el camino de streaming (case 'error'), en vez del
        // texto técnico en inglés.
        let _r = (raw || '') + '';
        if (_r.includes('unreachable') || _r.includes('not connected')){
            this._errorIsCancel = false;
            return this.tr('@cashguard_connection_failed');
        }
        let _code = this._mdwResultCode({ message: raw });
        this._errorIsCancel = (_code === 'CANCELLED');
        return this._mdwFriendlyMessage(_code) || raw;
    }

    // (El pre-check de cambio vía CONTENTS se eliminó del front: lo hace el
    // backend Rust con la caché del dispensable antes de arrancar la venta, y
    // devuelve CASH_SHORTAGE si no hay cambio. Así no hay un POST /Balance por
    // cada cobro metiendo ~3-4s de retardo.)

    private _armTransactionWatchdogs(): { startTimeout: any, progressTimeout: any } {
        this._lastProgressTime = null;

        let startTimeout = setTimeout(async () => {
            if (this.status == 'processing' && this.currentAmount == 0 && !this._lastProgressTime){
                console.error('[SMARTDRAWER] Transaction start timeout - no progress received after 2 minutes, releasing transaction');
                this.status = 'error';
                this.errorMessage = this.tr('@cashguard_start_failed');
                this.toast.ShowAlert('danger', this.tr('@cashguard_start_failed'), 3000);
                await this._cancelTransaction();
                setTimeout(() => {
                    this._dismissSelf(null);
                }, 1000);
            }
        }, 120000);
        this._timeouts.push(startTimeout);

        let progressTimeout = setTimeout(async () => {
            if (this.status == 'processing'){
                console.error('[SMARTDRAWER] Transaction timeout (2 minutes) - releasing transaction and closing modal');
                this.status = 'error';
                this.errorMessage = this.tr('@cashguard_transaction_error');
                this.toast.ShowAlert('danger', this.tr('@cashguard_transaction_error'), 3000);
                await this._cancelTransaction();
                setTimeout(() => {
                    this._dismissSelf(null);
                }, 1000);
            }
        }, 120000);
        this._timeouts.push(progressTimeout);

        return { startTimeout, progressTimeout };
    }

    private _isNotFirstQueuedError(message: string): boolean {
        if (!message){
            return false;
        }
        return message.includes('not first queued') ||
            (message.includes('TransactionId') && message.includes('not first'));
    }

    private _blockingIdsFromQueue(queue: Array<any>, ourTransactionId: number | string): Array<string> {
        let _ourId = ourTransactionId?.toString();
        return (queue || [])
            .filter(t => {
                let _tId = t?.transactionId?.toString();
                return _tId && _tId !== _ourId;
            })
            .sort((a, b) => (a.placeInQueue || 999) - (b.placeInQueue || 999))
            .map(t => t.transactionId.toString());
    }

    private async _refreshBlockingTransactionIds(): Promise<void> {
        let _queue = await this._fetchQueue();
        this.blockingTransactionIds = this._blockingIdsFromQueue(_queue, this._transactionId);
    }

    private async _setQueueConflictError(rawMessage: string): Promise<void> {
        await this._refreshBlockingTransactionIds();
        if (this.blockingTransactionIds.length > 0){
            this.errorMessage = this.tr('@cashguard_queue_blocked', [this.blockingTransactionIds.join(', ')]);
        } else {
            this.errorMessage = rawMessage || this.tr('@cashguard_start_failed');
        }
    }

    private _parseQueuePayload(data: any): Array<any> | null {
        if (data == null || data === undefined){
            return null;
        }

        let messageData = data;
        if (typeof messageData === 'string'){
            try {
                messageData = JSON.parse(messageData);
            } catch(e){
                return null;
            }
        }

        if (messageData && typeof messageData === 'object' && Array.isArray(messageData.queue)){
            return messageData.queue;
        }

        return null;
    }

    private async _fetchQueue(): Promise<Array<any> | null> {
        if (!this._connection){
            return null;
        }

        // La respuesta de GET_QUEUE llega como terminal del Send (mismo
        // contrato que client_cashguard.py / {"command":"GET_QUEUE","queue":[],...}).
        // Escuchar solo OnMessageRecv falla en WSR HTTP donde no hay streaming.
        let sendResponse = await Promise.race([
            this._connection.Send(this._target, { command: 'GET_QUEUE' }),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 10000))
        ]);

        let queueFromSend = this._parseQueuePayload(sendResponse?.data);
        if (queueFromSend !== null){
            console.log('[SMARTDRAWER] GET_QUEUE via Send:', queueFromSend);
            return queueFromSend;
        }

        // El guard de arriba ya no vale: el Send anterior espera hasta 10s y en esa
        // ventana el usuario puede abortar, que llama a _Disconnect() y deja
        // _connection a null. Sin este recheck el fallback revienta con "Cannot read
        // properties of null" y el TypeError sube hasta el catch de la transaccion,
        // que lo reporta como "Error starting transaction" — enmascarando el motivo.
        if (!this._connection){
            return null;
        }

        // Fallback: servicios que emiten la cola por OnMessageRecv sin resolver Send.
        let queueSubscription: any = null;
        let queuePromise = new Promise<Array<any> | null>((resolve) => {
            queueSubscription = this._connection.OnMessageRecv.subscribe((data: any) => {
                let queue = this._parseQueuePayload(data?.data);
                if (queue !== null){
                    queueSubscription.unsubscribe();
                    resolve(queue);
                }
            });
        });

        this._connection.Send(this._target, { command: 'GET_QUEUE' });

        let queueFromStream = await Promise.race([
            queuePromise,
            new Promise<null>((resolve) => {
                setTimeout(() => {
                    if (queueSubscription){
                        queueSubscription.unsubscribe();
                    }
                    resolve(null);
                }, 10000);
            })
        ]);

        if (queueSubscription){
            queueSubscription.unsubscribe();
        }

        if (queueFromStream !== null){
            console.log('[SMARTDRAWER] GET_QUEUE via OnMessageRecv:', queueFromStream);
            return queueFromStream;
        }

        console.warn('[SMARTDRAWER] GET_QUEUE failed — no response');
        return null;
    }

    private async _preflightClearStaleTransactions(): Promise<void> {
        let _queue = await this._fetchQueue();
        if (!_queue || _queue.length === 0){
            return;
        }

        let _blocking = this._blockingIdsFromQueue(_queue, this._transactionId);
        if (_blocking.length === 0){
            return;
        }

        console.log('[SMARTDRAWER] Preflight: clearing stale queue entries:', _blocking.join(', '));
        await this._releaseTransactionsList(_queue.filter(t => _blocking.includes(t.transactionId?.toString())));
    }

    private async _releaseSingleTransaction(blockingTransaction: any): Promise<boolean> {
        if (!this._connection || !blockingTransaction){
            return false;
        }

        let blockingId = blockingTransaction.transactionId;
        let blockingTransactionId = this._wireTxId(blockingId);
        let amountDue = blockingTransaction.amountDue || 0;
        let status = blockingTransaction.status || '';

        console.log('[SMARTDRAWER] Releasing blocking transaction:', blockingTransactionId, 'status:', status);

        if (status === 'InQueue' || status === 'PendingActivation'){
            let initiateResponse = await Promise.race([
                this._connection.Send(this._target, {
                    command: 'INITIATE_TRANSACTION',
                    transaction_id: blockingTransactionId,
                    amount_due: amountDue
                }),
                new Promise<null>((resolve) => setTimeout(() => resolve(null), 10000))
            ]);
            if (initiateResponse?.exec == 'SUCCESS'){
                await new Promise(resolve => setTimeout(resolve, 200));
            }
        }

        let releaseResponse = await Promise.race([
            this._connection.Send(this._target, {
                command: 'RELEASE_TRANSACTION',
                transaction_id: blockingTransactionId,
                // La tx que liberamos es OTRA (una atascada en la cola), NO el
                // cobro actual: usar `this._cashless` (tipo del cobro en curso)
                // haria que el back cancelara sobre el dispositivo equivocado.
                // La cola es la del reciclador de efectivo (CashGuard; PROSEGUR
                // no encola cobros de tarjeta), asi que la tx atascada es SIEMPRE
                // de efectivo -> cashless:false (cancelar sobre el reciclador).
                cashless: false
            }),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 120000))
        ]);

        if (releaseResponse?.exec == 'SUCCESS'){
            return true;
        }

        let errorMsg = releaseResponse?.data || '';
        if (errorMsg.includes('not found')){
            return true;
        }

        // Doc REST 4.2: DELETE transaction from queue si RELEASE no basta
        let deleteResponse = await Promise.race([
            this._connection.Send(this._target, {
                command: 'DELETE_FROM_QUEUE',
                transaction_id: blockingTransactionId
            }),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 10000))
        ]);
        return deleteResponse?.exec == 'SUCCESS';
    }

    private async _releaseTransactionsList(transactions: Array<any>): Promise<boolean> {
        if (!transactions || transactions.length === 0){
            return false;
        }

        let _allReleased = true;
        for (let _tx of transactions){
            let _released = await this._releaseSingleTransaction(_tx);
            if (!_released){
                _allReleased = false;
            }
            await new Promise(resolve => setTimeout(resolve, 200));
        }

        await new Promise(resolve => setTimeout(resolve, 500));
        return _allReleased;
    }

    /**
     * Releases a blocking transaction from the queue.
     * This is called when PROCESS_TRANSACTION fails with "not first queued" error.
     * It gets the queue, finds the first transaction (that's not ours), and releases it.
     * 
     * @param ourTransactionId The ID of our transaction (to avoid releasing it)
     * @returns true if a blocking transaction was found and released, false otherwise
     */
    private async _releaseBlockingTransaction(ourTransactionId: number | string): Promise<boolean> {
        try {
            if (!this._connection){
                console.error('[SMARTDRAWER] Cannot release blocking transaction - no connection');
                return false;
            }

            console.log('[SMARTDRAWER] Getting queue to find blocking transaction...');
            let queue = await this._fetchQueue();
            if (!queue){
                console.error('[SMARTDRAWER] Failed to get queue data');
                return false;
            }

            console.log('[SMARTDRAWER] Queue contents:', queue);
            let blockingTransactions = queue
                .filter(t => this._blockingIdsFromQueue([t], ourTransactionId).length > 0)
                .sort((a, b) => (a.placeInQueue || 999) - (b.placeInQueue || 999));

            if (blockingTransactions.length === 0){
                console.log('[SMARTDRAWER] No blocking transactions found in queue');
                return false;
            }

            console.log('[SMARTDRAWER] Found', blockingTransactions.length, 'blocking transaction(s) to release');
            await this._releaseTransactionsList(blockingTransactions);
            return true;
        } catch(e){
            console.error('[SMARTDRAWER] Error releasing blocking transaction:', e);
            return false;
        }
    }

    private async _cancelTransaction(reconcile = false, forceDedicated = false){
        // Prevent multiple simultaneous cancellation attempts
        if (this._cancelling){
            console.log('[SMARTDRAWER] Cancellation already in progress, skipping');
            return;
        }

        if (!this._transactionId){
            console.warn('[SMARTDRAWER] Cannot cancel transaction - transactionId:', this._transactionId);
            return;
        }

        // Only cancel if transaction is still active. En modo `reconcile` se
        // manda igualmente: es una alineacion de estado, y el modulo PROSEGUR
        // responde no-op (`NO_TRANSACTION_IN_PROGRESS`) si no habia nada abierto.
        if (!reconcile && this.status !== 'waiting' && this.status !== 'processing' && this.status !== 'error'){
            console.log('[SMARTDRAWER] Transaction already completed/cancelled, status:', this.status);
            return;
        }

        this._cancelling = true;
        
        try {
            // Número solo si es COMPLETAMENTE decimal (objid); si es UUID o
            // serie+factura se envía la cadena tal cual, sin truncar por parseInt.
            let transactionId = this._wireTxId(this._transactionId);

            console.log('[SMARTDRAWER] Releasing transaction to return money:', transactionId, 'Current amount:', this.currentAmount);
            
            if (!this._place){
                console.error('[SMARTDRAWER] Cannot release transaction - place is null');
                return;
            }

            // 1) Intentamos primero con la conexion existente del modal.
            //    En WSR (HTTP) el devsvc.Connect anterior abria una nueva
            //    fetch que podia colgarse si el rasppi seguia procesando
            //    el PROCESS_TRANSACTION original — el cliente veia 10s
            //    de timeout y devolvia null. Y en WSR streaming la conexion
            //    multiplexa por UUID, asi que reusarla es lo natural.
            //
            //    El RELEASE_TRANSACTION va con su propio UUID y el Send no
            //    interfiere con el PROCESS_TRANSACTION en vuelo: el rasppi
            //    spawna cada mensaje en su propia task (Rust tokio::spawn).
            let connectionToUse: WSConnection = null;
            let _ownDedicated = false;

            // En modo `reconcile` (o abort/close, forceDedicated) NO se reutiliza
            // la conexion del modal: el dismiss dispara ngOnDestroy, que la
            // desconecta, y este envio es fire-and-forget — nos quedariamos a
            // medias justo en el caso que esto viene a cubrir (el abort no
            // llegaba al cajon, no devolvia el importe ni cancelaba PROSEGUR).
            // Con una conexion propia el envio sobrevive al cierre del modal y
            // se cierra sola en el finally.
            if (this._connection && !reconcile && !forceDedicated){
                console.log('[SMARTDRAWER] Reusing existing connection for RELEASE_TRANSACTION');
                connectionToUse = this._connection;
            }
            else {
                // 2) Sin _connection (o reconciliando): abrimos una dedicada.
                console.log('[SMARTDRAWER] No existing connection — creating new dedicated for RELEASE_TRANSACTION');
                let _host = this._place.wshost;
                let _port = this._place.wsport ? parseInt(this._place.wsport) : 5005;
                let _name = this._place.name;

                connectionToUse = await Promise.race([
                    this.devsvc.Connect(_host, _port, _name, this._tolocal),
                    new Promise<null>((resolve) => setTimeout(() => resolve(null), 10000))
                ]);

                if (!connectionToUse){
                    console.error('[SMARTDRAWER] Failed to create new connection for RELEASE_TRANSACTION');
                    return;
                }
                _ownDedicated = true;
            }
            
            // Use RELEASE_TRANSACTION to properly return any money that was introduced
            // This command handles the full release process including returning the money
            // Timeout of 2 minutes to allow the service to process and respond
            console.log('[SMARTDRAWER] Sending RELEASE_TRANSACTION for transaction:', transactionId, 'waiting up to 2 minutes for response...');
            
            try {
                let releaseResponse = await Promise.race([
                    connectionToUse.Send(this._target, {
                        command: 'RELEASE_TRANSACTION',
                        transaction_id: transactionId,
                        // Agnostico e igual que _releaseSingleTransaction: si es
                        // cobro con tarjeta, el back cancela sobre el datafono
                        // (PaymentTerminal) en vez del reciclador de efectivo.
                        // Sin esto, abortar un cobro cashless cancelaba el
                        // reciclador y dejaba el terminal inconsistente.
                        cashless: this._cashless,
                        // PROSEGUR (mismo motivo que en _txPayload/CHARGE_CASHLESS):
                        // el datafono destino. Sin esto el back cae a
                        // PaymentTerminal y, con dos datafonos, abortar podia
                        // cancelar el equivocado y dejar PaymentTerminal2 con la
                        // venta viva. Solo se envia si lo teniamos guardado del
                        // cobro; el back ignora el campo en efectivo.
                        ...(this._deviceid ? { deviceId: this._deviceid } : {}),
                        // Tope de seguridad para la devolucion del deposito
                        // (PROSEGUR): lo que el POS vio entrar, en centimos. El
                        // back nunca dispensa mas que esto.
                        current_amount: Math.round((this.currentAmount || 0) * 100)
                    }),
                    new Promise<null>((resolve) => setTimeout(() => resolve(null), 120000)) // 2 minutes = 120000ms
                ]);
                
                console.log('[SMARTDRAWER] RELEASE_TRANSACTION response received:', releaseResponse);
                
                if (releaseResponse){
                    if (releaseResponse.exec == 'SUCCESS'){
                        console.log('[SMARTDRAWER] Release transaction successful:', releaseResponse);
                    } else {
                        // Check if the data contains success status (the Rust service may return it this way)
                        let responseData = releaseResponse.data;
                        if (responseData){
                            // Handle string data that might be JSON
                            if (typeof responseData === 'string'){
                                try {
                                    responseData = JSON.parse(responseData);
                                } catch(e){
                                    // Not JSON, treat as string
                                }
                            }
                            
                            // Check if it's actually a success message
                            if (responseData && typeof responseData === 'object' && responseData.status === 'success'){
                                console.log('[SMARTDRAWER] Release transaction successful (status in data):', responseData);
                            } else {
                                console.warn('[SMARTDRAWER] RELEASE_TRANSACTION returned error:', releaseResponse);
                            }
                        } else {
                            console.warn('[SMARTDRAWER] RELEASE_TRANSACTION returned error:', releaseResponse);
                        }
                    }
                } else {
                    // Timeout after 2 minutes - log warning
                    console.error('[SMARTDRAWER] RELEASE_TRANSACTION timeout after 2 minutes - no response received');
                    // Even on timeout, assume the release was attempted (the service may have processed it)
                    console.log('[SMARTDRAWER] Assuming transaction was released (timeout but service may have processed it)');
                }
            } catch(e){
                console.error('[SMARTDRAWER] Exception while waiting for RELEASE_TRANSACTION response:', e);
            } finally {
                // Solo cerramos la conexion si la abrimos nosotros (fallback
                // "new dedicated"). Si reutilizamos this._connection no la
                // tocamos — ngOnDestroy se encarga de su lifecycle.
                if (_ownDedicated && connectionToUse){
                    console.log('[SMARTDRAWER] Closing dedicated release connection');
                    this.devsvc.Disconnect(connectionToUse);
                }
            }
        } catch(e){
            console.error('[SMARTDRAWER] Error releasing transaction:', e);
        } finally {
            // Reset flag after a short delay to allow the release to complete
            setTimeout(() => {
                this._cancelling = false;
            }, 1000);
        }
    }

    Price(price){
        if (price == null || price === undefined || isNaN(price)){
            return "0,00";
        }
        let _parts = Math.abs(price).toString().split('.');

        let _int = _parts[0];
        let _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : "00";

        return _int + ',' + _dec;
    }

    get AmountMissing(){
        if (this.currentAmount == null || this.amountDue == null){
            return 0;
        }
        return Math.max(0, this.amountDue - this.currentAmount);
    }

    /**
     * ¿Debe bloquearse ABORTAR? En un cobro en EFECTIVO por el cajon EN CURSO,
     * cuando el dinero introducido ya alcanza (o supera) el importe a cobrar, el
     * pago esta hecho: el modal esta a punto de pasar a 'success'. En ese ~1s
     * abortar devolveria el dinero YA entregado por el cliente -> descuadre. Se
     * bloquea el boton (queda atenuado) para no cancelar por error.
     *
     * Se exime SOLO el estado 'error' (p.ej. SHORTAGE: se metio dinero pero la
     * maquina no pudo dar el cambio y el ticket queda SIN pagar): ahi el cobro NO
     * se completo, asi que el operario DEBE poder abortar/cerrar (antes quedaba
     * atascado porque currentAmount >= amountDue bloqueaba tambien ahi).
     *
     * En 'success' (o 'success' con changeShortage, que mantiene el modal abierto)
     * el efectivo YA se retuvo y el ticket esta PAGADO: se MANTIENE bloqueado, para
     * que la X no dispare un dismiss(null) que los llamadores tratan como
     * cancelado/no pagado (dejaria un ticket cobrado como impagado). NO aplica a
     * tarjeta (cashless): no hay progresion de importe y el abort sigue vivo.
     */
    // Primeros ABORT_ARM_MS tras pulsar PAGAR o tras cada envío del cobro: ver
    // el comentario de _abortArmUntil. Solo mientras se espera/procesa (un
    // error o la cancelación lo desbloquean de inmediato para poder cerrar).
    get AbortArming(): boolean {
        return (this.status === 'waiting' || this.status === 'processing')
            && !this._cancelling
            && Date.now() < this._abortArmUntil;
    }

    // Arma la ventana de bloqueo del abort. Se llama al PULSAR PAGAR (ngOnInit)
    // y ANTES de CADA envío del cobro (arranque, reintento tras LOGIN, reintento
    // tras limpiar la cola y RetryTransaction): cada envío arranca una
    // activación nueva en el cajón/datáfono y, si las esperas previas
    // consumieron la ventana anterior, el abort volvería a entrar en la carrera
    // (Copilot #1812). El timeout solo fuerza el repintado al vencer; va en
    // _abortArmTimer (propio, no en _timeouts) para que ngOnDestroy lo limpie.
    private _armAbortWindow(){
        this._abortArmUntil = Date.now() + UiCashGuardCashPayModal.ABORT_ARM_MS;
        if (this._abortArmTimer){
            clearTimeout(this._abortArmTimer);
        }
        this._abortArmTimer = setTimeout(() => {
            this._abortArmTimer = null;
            console.log('[SMARTDRAWER] Ventana de arranque vencida: abort habilitado');
        }, UiCashGuardCashPayModal.ABORT_ARM_MS);
    }

    get AbortLocked(): boolean {
        if (this.AbortArming){
            return true;
        }
        return !this._cashless
            && this.status !== 'error'
            && this.currentAmount != null
            && this.amountDue != null
            && this.currentAmount >= this.amountDue;
    }

    /**
     * PROSEGUR: alinea el estado de la maquina antes de cerrar.
     *
     * El MDW puede quedarse `In Transaction` con el efectivo dentro aunque el
     * front haya dado el cobro por terminado. A partir de ahi TODO lo que toque
     * el reciclador (otro cobro, una devolucion, una dispensa) muere con
     * `TRANSACTION_IN_PROGRESS`, y el dinero queda atrapado.
     *
     * Se manda un RELEASE_TRANSACTION que el modulo traduce a CancelSale: si la
     * venta ya termino, el MDW contesta `NO_TRANSACTION_IN_PROGRESS` y no se
     * toca nada; si seguia abierta, se cierra y se devuelve lo depositado.
     *
     * Acotado a PROSEGUR a proposito: CASHGUARD y CASHLOGY tienen su propia
     * gestion de cola y no se tocan.
     */
    private _reconcileProsegurState(){
        if (this._target !== 'PROSEGUR'){
            return;
        }
        console.log('[SMARTDRAWER] PROSEGUR: alineando estado de la maquina antes de cerrar');
        this._releaseTransactionInBackground('Prosegur reconcile', true);
    }

    async AcceptModal() {
        this._reconcileProsegurState();

        // Return the amount in euros (not cents)
        // Always use amountDue for consistency (this is what was requested to be paid)
        // For mixed payments, amountDue is the cash portion (e.g., 3€)
        // For regular payments, amountDue equals the ticket price
        let dismissData: any = {
            amount: this.amountDue,
            refund: this.amountRefund
        };
        // Propagar cashlessInfo del cobro cashless (transactionNumber) para que
        // el parent lo cachee y pueda reembolsar despues (comando REFUND).
        if (this._cashlessInfo){
            dismissData.cashlessInfo = this._cashlessInfo;
        }
        // PROSEGUR: propagar el orderId del cobro para poder hacer el CardRefund.
        if (this._orderId){
            dismissData.orderId = this._orderId;
        }
        await this._dismissSelf(dismissData);
    }    

    private _releaseTransactionInBackground(context: string, reconcile = false){
        // `reconcile` = alineacion defensiva al cerrar (solo PROSEGUR): se salta
        // la guarda de 'success' porque el objetivo es justo el caso en que el
        // front cree que termino y la maquina sigue dentro de la venta.
        if (!this._transactionId || this._cancelling){
            return;
        }
        if (!reconcile && (this.status == 'success')){
            return;
        }
        console.log(`[SMARTDRAWER] ${context} - releasing transaction in background:`, this._transactionId, 'status:', this.status);

        let _txId = this._wireTxId(this._transactionId);

        if (this.HasQueueConflict){
            this._releaseBlockingTransaction(_txId).catch(e => {
                console.error('[SMARTDRAWER] Error clearing blocking queue in background:', e);
            });
        }

        // forceDedicated=true: este release es fire-and-forget y el modal se
        // esta cerrando (Abort/Close/Manual), asi que ngOnDestroy va a
        // desconectar this._connection. Con una conexion propia el
        // RELEASE_TRANSACTION sobrevive al cierre y llega al cajon (devuelve el
        // importe / cancela PROSEGUR); reutilizar la del modal lo cortaba a medias.
        this._cancelTransaction(reconcile, true).then(() => {
            console.log('[SMARTDRAWER] Transaction released in background');
        }).catch(e => {
            console.error('[SMARTDRAWER] Error releasing transaction in background:', e);
        });
    }

    async ClearBlockingQueue(){
        if (this._clearingQueue || !this._connection){
            return;
        }

        this._clearingQueue = true;
        try {
            let _txId = this._wireTxId(this._transactionId);
            await this._releaseBlockingTransaction(_txId);
            await this._refreshBlockingTransactionIds();

            if (this.blockingTransactionIds.length === 0){
                this.errorMessage = this.tr('@cashguard_queue_cleared');
                this._queueReadyToRetry = true;
            }
        } finally {
            this._clearingQueue = false;
        }
    }

    async RetryTransaction(){
        if (!this._connection || this._clearingQueue || this.status == 'success'){
            return;
        }

        this._clearingQueue = true;
        this._queueReadyToRetry = false;
        this.status = 'processing';
        this.errorMessage = null;
        this.blockingTransactionIds = [];

        try {
            let _txId = this._wireTxId(this._transactionId);
            let _amountDueCents = Math.round(this.amountDue * 100);

            this._clearAllTimeouts();
            let { startTimeout, progressTimeout } = this._armTransactionWatchdogs();

            await this._releaseBlockingTransaction(_txId);
            await this._preflightClearStaleTransactions();

            // Mismo guard que el arranque y el reintento tras LOGIN: durante los
            // dos await de arriba los botones siguen activos y el cajero puede
            // abortar (o el modal destruirse y dejar _connection a null). Mandar
            // aquí un cobro nuevo con el RELEASE ya en vuelo recrea la venta
            // huérfana que esta PR evita (Copilot #1812). La cancelación gana.
            if (this._cancelling || !this._connection){
                console.warn('[SMARTDRAWER] Conexión cerrada o cobro cancelado antes del reintento de cola — se aborta');
                this._clearAllTimeouts();
                return;
            }

            this._armAbortWindow();
            let _retryResponse = await Promise.race([
                this._connection.Send(this._target, this._txPayload(_txId, _amountDueCents)),
                new Promise<null>((resolve) => setTimeout(() => resolve(null), 300000))
            ]);

            clearTimeout(startTimeout);
            this._timeouts = this._timeouts.filter(t => t !== startTimeout);

            if (_retryResponse?.exec == 'SUCCESS' && _retryResponse.data){
                this._handleMessage(_retryResponse);
            } else if (_retryResponse?.exec != 'SUCCESS'){
                let _errorMsg = _retryResponse?.data || '';
                if (_errorMsg.includes('already being processed') || _errorMsg.includes('already processed')){
                    console.log('[SMARTDRAWER] Retry: transaction already processing, waiting for progress...');
                } else if (this._isNotFirstQueuedError(_errorMsg)){
                    clearTimeout(progressTimeout);
                    this._timeouts = this._timeouts.filter(t => t !== progressTimeout);
                    this.status = 'error';
                    await this._setQueueConflictError(_errorMsg);
                } else {
                    clearTimeout(progressTimeout);
                    this._timeouts = this._timeouts.filter(t => t !== progressTimeout);
                    this.status = 'error';
                    // Mismo mapeo que el arranque: MIDDLEWARE_UNREACHABLE / DEVICE_OFFLINE /
                    // NACK / CANCELLED… en claro también tras REINTENTAR (Copilot #1812).
                    this.errorMessage = _errorMsg ? this._friendlyRawError(_errorMsg) : this.tr('@cashguard_start_failed');
                }
            }
        } catch(e){
            this.status = 'error';
            this.errorMessage = this.tr('@cashguard_start_failed');
            console.error('[SMARTDRAWER] Error retrying transaction:', e);
        } finally {
            this._clearingQueue = false;
        }
    }

    async CloseModal() {
        // Con un cobro EN CURSO, cerrar con la X NO puede ser fire-and-forget: si
        // el RELEASE no llega, el dispositivo queda con una venta colgada (zombi)
        // que bloquea el siguiente cobro con BUSY (sobre todo el datafono
        // PROSEGUR). Se trata igual que ABORTAR: se ESPERA la cancelacion (con
        // tope de 12s, mostrando "Abortando pago") para que llegue al
        // datafono/cajon antes de cerrar. Fuera de un cobro en curso
        // (success/error/idle) se mantiene el cierre rapido de siempre.
        if ((this.status == 'waiting' || this.status == 'processing') && !this._cancelling){
            await this.AbortModal();
            return;
        }

        this._releaseTransactionInBackground('Closing modal');

        // Resume keepalive when closing modal
        if (this._connection && typeof this._connection.ResumeKeepAlive === 'function'){
            this._connection.ResumeKeepAlive();
            console.log('[SMARTDRAWER] Keepalive resumed on modal close');
        }

        await this._dismissSelf(null);
    }

    async AbortModal() {
        if (this._cancelling){
            // Abort YA en curso: IGNORAR pulsaciones repetidas (X / ABORTAR).
            // NO forzar el cierre aqui. Cerrar a media cancelacion sacaba el
            // modal antes de que el RELEASE llegara al cajon/datafono y permitia
            // relanzar un cobro al instante -> venta ZOMBI que cuelga el device
            // (doble evento). El propio AbortModal cierra SOLO al terminar el
            // release (con tope de 12s en el Promise.race de abajo), asi que la
            // salida esta garantizada sin necesidad de un cierre forzado. La X
            // ademas va [disabled]="IsCancelling", asi que este camino solo
            // salta ante una carrera de eventos: se descarta.
            console.warn('[SMARTDRAWER] Abort ya en curso — se ignora la pulsacion repetida');
            return;
        }
        // Efectivo con el importe ya alcanzado: el pago esta hecho, no se aborta
        // (devolveria el dinero entregado). Cubre tambien la X, que delega aqui.
        if (this.AbortLocked){
            console.log(this.AbortArming
                ? '[SMARTDRAWER] Abort bloqueado: el cobro aún está arrancando (ventana de arranque)'
                : '[SMARTDRAWER] Abort bloqueado: importe ya alcanzado');
            return;
        }
        console.log('[SMARTDRAWER] Aborting transaction...');

        if (this.HasQueueConflict){
            let _txId = this._wireTxId(this._transactionId);
            this._releaseBlockingTransaction(_txId).catch(e => {
                console.error('[SMARTDRAWER] Error clearing blocking queue on abort:', e);
            });
        }

        // El RELEASE va por una conexion DEDICADA (forceDedicated=true): asi
        // SOBREVIVE al cierre del modal y llega al cajon (devuelve el importe /
        // cancela) aunque cerremos. Mostramos "Abortando pago" (IsCancelling)
        // mientras esperamos su confirmacion, PERO con un TOPE: si el middleware
        // no responde en unos segundos, cerramos igual y el release sigue en
        // segundo plano (el RS barre cualquier venta que quede en curso).
        //
        // Antes: se esperaba hasta 2 min (el modal se quedaba colgado en
        // "Abortando pago"); o se cerraba de golpe reusando la conexion del
        // modal, que ngOnDestroy cortaba a media cancelacion. Este punto medio
        // no cuelga y no deja el release a medias.
        // X == ABORT SIEMPRE (efectivo Y tarjeta): forzamos reconcile=true para que el
        // RELEASE se mande AUNQUE el status no esté en waiting/processing/error (en tarjeta
        // el cobro podía estar en un estado que hacía que _cancelTransaction saliera por el
        // early-return SIN abortar -> el modal se cerraba al instante dejando la venta VIVA
        // en el datáfono (huérfana -> cuelga el MDW)). En reconcile el MDW responde no-op
        // (NO_TRANSACTION_IN_PROGRESS) si ya no había nada abierto, así que es seguro.
        const _release = this._cancelTransaction(true, true).catch(e => {
            console.error('[SMARTDRAWER] Error aborting transaction:', e);
        });
        await Promise.race([
            _release,
            new Promise<void>(resolve => setTimeout(resolve, 12000))
        ]);

        // Resume keepalive when aborting
        if (this._connection && typeof this._connection.ResumeKeepAlive === 'function'){
            this._connection.ResumeKeepAlive();
            console.log('[SMARTDRAWER] Keepalive resumed on abort');
        }

        await this._dismissSelf(null);
    }

    async ManualFallback() {
        // Cajon roto / no conecta (error): salir a cobro manual (efectivo normal).
        // Libera cualquier transaccion pendiente en el CashGuard y cierra con
        // { fallback: true } — el padre ya maneja esa flag abriendo el modal de
        // efectivo estandar (mismo camino que el auto-fallback por error de
        // dispositivo). Asi el cajero puede cerrar la venta aunque el cajon falle.
        console.log('[SMARTDRAWER] Manual cash fallback requested by cashier');
        this._releaseTransactionInBackground('Manual fallback');

        if (this._connection && typeof this._connection.ResumeKeepAlive === 'function'){
            this._connection.ResumeKeepAlive();
        }

        await this._dismissSelf({ fallback: true });
    }
}
